import _find from 'lodash/find';
import _get from 'lodash/get';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import I18n from 'i18n-js';
import { switchMap, mergeMap, catchError } from 'rxjs/operators';
import { ofType, combineEpics } from 'redux-observable';
import { EMPTY, of, merge, concat, timer, forkJoin } from 'rxjs';
import { FIELD_NAME } from 'views/v2/home/constants';
import { CASH_BY_SENDER, QUOTE_OPTIMIZED } from 'constants/booking-constants';
import {
  FETCH_SHOPIFY_SHOPS,
  SET_SHOP,
  UPDATE_SHOPIFY_MULTIPLE_ORDERS,
  FETCH_SHOPIFY_QUOTE,
  UPDATE_SHOPIFY_MSD_ORDER,
  CREATE_SHOPIFY_DELIVERIES,
  LOCATION_DRAWER_SHOPIFY_ROUTE_OPTIMIZATION,
  updateShopifyMultipleOrdersShallowAction,
  updateShopifyMsdOrderShallowAction,
  fetchShopifyQuoteAction,
  UPDATE_SHOPIFY_MULTIPLE_ORDERS_QUOTES_BEFORE_CREATE_BOOKING,
  UPDATE_SHOPIFY_MSD_ORDER_QUOTE_BEFORE_CREATE_BOOKING,
} from 'actions/shopify-actions';

import {
  toGetShopifyMultipleOrdersBatchQuote,
  toGetShopifyMultipleOrdersQuote,
  toGetShopifyMsdOrdersQuote,
} from 'helper/shopify';
import { getQuoteToRender, toRouteOptimisationRequest } from 'helper/order';
import { getQuoteErrNotiObservable } from 'helper/quote';
import { getCompanyID } from 'helper/user';
import { getAlternativeFare, getOptimizedSteps } from 'helper/quoteV2-helper';
import { shopifyMultipleOrdersSelector } from 'selectors/shopify-selectors';
import {
  getFailedNotification,
  getSuccessNotification,
} from 'views/v2/home/order-notification';
import { pushNotificationAction } from 'actions/app-actions';
import { extractErrors } from './booking-epics';
import { getOptimizedRoute } from './epic-helpers';
import { NOTIFICATION_TYPE } from '../../components/v2/notification/notification';

/**
 * Helpers
 */

const getAllowedPaymentMethods = state => {
  const userPayments = _get(state, 'booking.paymentMethods.result', []);

  // get the non-cash payment methods
  const allowedPayments = [];
  const primaryPayments = userPayments.filter(
    x => !!x.isPrimary && !!x.enabled && !!x.isPaymentAllowed,
  );
  const nonPrimaryPayment = userPayments.filter(
    x => !x.isPrimary && !!x.enabled && !!x.isPaymentAllowed,
  );
  if (!_isEmpty(primaryPayments)) allowedPayments.push(...primaryPayments);
  if (!_isEmpty(nonPrimaryPayment)) allowedPayments.push(...nonPrimaryPayment);

  return allowedPayments;
};

/**
 * Epics
 */

const refreshShopEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_SHOPIFY_SHOPS.SUCCESS),
    switchMap(action => {
      const state = state$.value;

      // do nothing if currently has no selected shop
      if (!state.shopify.shop) return EMPTY;

      const shopIdentifier = state.shopify.shop.shop;
      const shops = state.shopify.shops || [];

      const foundShop = _find(shops, { shop: shopIdentifier });

      return of({
        type: SET_SHOP,
        payload: foundShop,
      });
    }),
  );

const fetchShopifyMultipleOrderQuotesBeforeCreateBookingEpic = (
  action$,
  state$,
) =>
  action$.pipe(
    ofType(UPDATE_SHOPIFY_MULTIPLE_ORDERS_QUOTES_BEFORE_CREATE_BOOKING),
    mergeMap(action => {
      const state = state$.value;
      const orders = action.payload;
      const userInfo = state.app.userInfo;
      // get the non-cash payment methods
      const payments = getAllowedPaymentMethods(state);

      const firstPaymentTypeID = _isEmpty(payments)
        ? CASH_BY_SENDER
        : _get(payments[0], 'paymentTypeID', '');

      const observables = orders.map((order, index) => {
        // use the first payment method that will appear on the screen
        order[FIELD_NAME.PAYMENT_METHOD].value = firstPaymentTypeID;

        return of(
          updateShopifyMultipleOrdersShallowAction({
            index,
            order: {
              ...order,
              fetchingQuote: true,
            },
          }),
        );
      });

      // generate the quote request object
      const ordersToQuote = toGetShopifyMultipleOrdersBatchQuote(
        orders,
        userInfo,
        payments,
      );

      observables.push(
        merge(
          of(fetchShopifyQuoteAction(ordersToQuote, getCompanyID(userInfo))),
          action$.pipe(
            ofType(FETCH_SHOPIFY_QUOTE.SUCCESS),
            mergeMap(action2$ => {
              const quote = action2$.data;
              const orders2 = shopifyMultipleOrdersSelector(
                action2$.reduxState,
              );

              const observables = orders2.map((order, index) => {
                const newOrder = {
                  ...order,
                  quote: {
                    deliveries: [quote.deliveries[index]],
                  },
                  fetchingQuote: false,
                };

                const quoteToRender = getQuoteToRender(newOrder);
                newOrder.quote.forRender = quoteToRender;

                return concat(
                  of(
                    updateShopifyMultipleOrdersShallowAction({
                      index,
                      order: newOrder,
                    }),
                  ),
                  getQuoteErrNotiObservable(quoteToRender),
                );
              });

              return concat(...observables);
            }),
          ),
        ),
      );

      return concat(...observables);
    }),
  );

const fetchShopifyMultipleOrdersQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_SHOPIFY_MULTIPLE_ORDERS),
    switchMap(action => {
      const state = state$.value;
      const { order, index } = action.payload;
      const userInfo = state.app.userInfo;
      const payments = _get(state, 'booking.paymentMethods.result', []);
      const orderToQuote = toGetShopifyMultipleOrdersQuote(
        order,
        userInfo,
        payments,
      );
      return merge(
        of(
          updateShopifyMultipleOrdersShallowAction({
            index,
            order: {
              ...order,
              fetchingQuote: true,
            },
          }),
        ),
        of(fetchShopifyQuoteAction(orderToQuote, getCompanyID(userInfo))),
        action$.pipe(
          ofType(FETCH_SHOPIFY_QUOTE.SUCCESS),
          switchMap(action2 => {
            const newOrder = {
              ...order,
              quote: action2.data,
              fetchingQuote: false,
            };
            const quoteToRender = getQuoteToRender(newOrder);
            newOrder.quote.forRender = quoteToRender;
            return concat(
              of(
                updateShopifyMultipleOrdersShallowAction({
                  index,
                  order: newOrder,
                }),
              ),
              getQuoteErrNotiObservable(quoteToRender),
            );
          }),
        ),
      );
    }),
  );

const fetchShopifyMsdOrderQuoteBeforeCreateBookingEpic = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_SHOPIFY_MSD_ORDER_QUOTE_BEFORE_CREATE_BOOKING),
    switchMap(action => {
      const state = state$.value;
      const order = action.payload;
      const userInfo = state.app.userInfo;
      // get the non-cash payment methods
      const payments = getAllowedPaymentMethods(state);

      // use the first payment method that will appear on the screen
      order[FIELD_NAME.PAYMENT_METHOD].value = _isEmpty(payments)
        ? CASH_BY_SENDER
        : _get(payments[0], 'paymentTypeID', '');

      const orderToQuote = toGetShopifyMsdOrdersQuote(
        order,
        userInfo,
        payments,
      );

      return merge(
        of(
          updateShopifyMsdOrderShallowAction({
            order: {
              ...order,
              fetchingQuote: true,
            },
          }),
        ),
        of(fetchShopifyQuoteAction(orderToQuote, getCompanyID(userInfo))),
        action$.pipe(
          ofType(FETCH_SHOPIFY_QUOTE.SUCCESS),
          switchMap(action2 => {
            const newOrder = {
              ...order,
              quote: action2.data,
              fetchingQuote: false,
            };
            const quoteToRender = getQuoteToRender(newOrder);
            newOrder.quote.forRender = quoteToRender;
            return concat(
              of(
                updateShopifyMsdOrderShallowAction({
                  order: newOrder,
                }),
              ),
              getQuoteErrNotiObservable(quoteToRender),
            );
          }),
        ),
      );
    }),
  );

const fetchShopifyMsdOrderQuoteEpic = (action$, state$) =>
  action$.pipe(
    ofType(UPDATE_SHOPIFY_MSD_ORDER),
    switchMap(action => {
      const state = state$.value;
      const { order } = action.payload;
      const userInfo = state.app.userInfo;
      const payments = _get(state, 'booking.paymentMethods.result', []);
      const orderToQuote = toGetShopifyMsdOrdersQuote(
        order,
        userInfo,
        payments,
      );
      return merge(
        of(
          updateShopifyMsdOrderShallowAction({
            order: {
              ...order,
              fetchingQuote: true,
            },
          }),
        ),
        of(fetchShopifyQuoteAction(orderToQuote, getCompanyID(userInfo))),
        action$.pipe(
          ofType(FETCH_SHOPIFY_QUOTE.SUCCESS),
          switchMap(action2 => {
            const newOrder = {
              ...order,
              quote: action2.data,
              fetchingQuote: false,
            };
            const quoteToRender = getQuoteToRender(newOrder);
            newOrder.quote.forRender = quoteToRender;
            return concat(
              of(
                updateShopifyMsdOrderShallowAction({
                  order: newOrder,
                }),
              ),
              getQuoteErrNotiObservable(quoteToRender),
            );
          }),
        ),
      );
    }),
  );

const createShopifyDeliveriesSuccessEpic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_SHOPIFY_DELIVERIES.SUCCESS),
    switchMap(action => {
      const createdOrders = _get(action, 'data.deliveries', []);
      const totalOrderQty = createdOrders.length;
      const successOrderQty = createdOrders.reduce(
        (acc, order) => (_isEmpty(order.errors) ? acc + 1 : acc),
        0,
      );
      const failedOrderQty = totalOrderQty - successOrderQty;
      const errorMsgs = extractErrors(createdOrders);
      const notifications = _compact([
        getSuccessNotification(successOrderQty),
        getFailedNotification(failedOrderQty, totalOrderQty, errorMsgs, true),
      ]);

      return of(pushNotificationAction(notifications));
    }),
  );

const createShopifyDeliveriesFailEpic = (action$, state$) =>
  action$.pipe(
    ofType(CREATE_SHOPIFY_DELIVERIES.ERROR),
    switchMap(action => {
      const errorMessage = _get(action, 'data.message', '');
      const notifications = [getFailedNotification(1, 1, [errorMessage])];

      return of(pushNotificationAction(notifications));
    }),
  );

const failedToOptimizeRoute = errorMessage =>
  of(
    {
      type: LOCATION_DRAWER_SHOPIFY_ROUTE_OPTIMIZATION.ERROR,
    },
    pushNotificationAction({
      type: NOTIFICATION_TYPE.WARNING,
      message: I18n.t('v2.locationDrawer.failedToOptimizeRoute'),
      description:
        I18n.t('v2.locationDrawer.failedToOptimizeRouteDesc') +
        (errorMessage ? ` [${errorMessage}]` : ''),
    }),
  );

const shopifyRouteOptimizationEpic = (action$, state$) =>
  action$.pipe(
    ofType(LOCATION_DRAWER_SHOPIFY_ROUTE_OPTIMIZATION.START),
    mergeMap(() => {
      const state = state$.value;
      const order = _get(state, 'shopify.msdOrder', {});
      const userInfo = state.app.userInfo;
      const routeOptimisationRequest = toRouteOptimisationRequest(
        order,
        userInfo,
      );
      const optimizedRouteQuote$ = getOptimizedRoute(
        routeOptimisationRequest,
        getCompanyID(userInfo),
      ).pipe(catchError(error => of({ error })));
      // design is for this to always take at least 1 second
      const timer$ = timer(1000);
      return forkJoin(optimizedRouteQuote$, timer$);
    }),
    mergeMap(([result, _]) => {
      const err = _get(result, 'error');
      if (err) {
        return failedToOptimizeRoute(err);
      }

      const quoteV2 = _get(result, 'response.data.quote');
      if (!quoteV2) {
        return failedToOptimizeRoute();
      }

      const paxExpressError = _get(result, 'response.data.paxExpressError');
      if (paxExpressError) {
        return failedToOptimizeRoute(_get(paxExpressError, 'message'));
      }

      const state = state$.value;
      const alternativeFare = getAlternativeFare(quoteV2);
      const optimizedSteps = getOptimizedSteps(alternativeFare);
      if (
        alternativeFare &&
        optimizedSteps.length !==
          1 + _get(state, 'shopify.msdOrder.recipientLocations', []).length
      ) {
        return failedToOptimizeRoute();
      }

      return of({
        type: LOCATION_DRAWER_SHOPIFY_ROUTE_OPTIMIZATION.SUCCESS,
        payload: {
          quoteV2,
          optimizeQuotesStatus: {
            addressFieldsNeedReorder: !!alternativeFare,
            status: QUOTE_OPTIMIZED,
          },
        },
      });
    }),
  );

export default combineEpics(
  refreshShopEpic,
  fetchShopifyMultipleOrderQuotesBeforeCreateBookingEpic,
  fetchShopifyMultipleOrdersQuoteEpic,
  fetchShopifyMsdOrderQuoteBeforeCreateBookingEpic,
  fetchShopifyMsdOrderQuoteEpic,
  createShopifyDeliveriesSuccessEpic,
  createShopifyDeliveriesFailEpic,
  shopifyRouteOptimizationEpic,
);
